import * as React from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { getPillButtonStyle, responsivePadding } from '@stylesheet';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import LottieView from '@utils/lottieAnimation';
import { FrostedGlassGlow, getFrostedGlassStyle } from '@components/Global/FrostedCard/FrostedGlass';
import { DefaultRootState } from '@reduxLocal/persistState';

const CARD_RADIUS = 24;

const NotFoundScreen = ({ navigation }) => {
  const { t } = useTranslation();
  const theme = useSelector((state: DefaultRootState) => state.app.theme.lightTheme);

  const animationSource = React.useMemo(
    () =>
      theme
        ? require('@assets/animations/landing_Animation_dark.json')
        : require('@assets/animations/landing_Animation_light.json'),
    [theme]
  );

  const titleColor = theme ? '#111827' : '#F9FAFB';
  const mutedColor = theme ? '#6B7280' : '#9CA3AF';
  const accentColor = theme ? '#3b82f6' : '#60a5fa';

  const goHome = () => navigation.replace(Platform.OS === 'web' ? 'Loading' : 'LoadingScreen');

  return (
    <View style={styles.screen}>
      <View style={[styles.card, getFrostedGlassStyle(theme)]}>
        <FrostedGlassGlow theme={theme} clip borderRadius={CARD_RADIUS} />
        <View style={styles.animationContainer}>
          <LottieView style={styles.animation} source={animationSource} autoPlay loop={false} />
        </View>
        <Text style={[styles.code, { color: accentColor }]}>404</Text>
        <Text style={[styles.title, { color: titleColor }]}>{t("This page doesn't exist.")}</Text>
        <Text style={[styles.hint, { color: mutedColor }]}>{t('notFoundSupportHint')}</Text>
        <Pressable
          onPress={goHome}
          accessibilityRole="button"
          style={({ pressed }) => [getPillButtonStyle(theme, 'primary'), styles.cta, pressed && styles.ctaPressed]}
        >
          <Ionicons name="arrow-back" size={18} color="#fff" />
          <Text style={styles.ctaLabel}>{t('backToSmartSaaS')}</Text>
        </Pressable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: responsivePadding(24),
  },
  card: {
    width: '100%',
    maxWidth: 520,
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: CARD_RADIUS,
    paddingHorizontal: responsivePadding(24),
    paddingVertical: responsivePadding(32),
    overflow: 'hidden',
  },
  animationContainer: {
    width: '100%',
    height: 200,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  animation: {
    width: '100%',
    height: '100%',
    backgroundColor: 'transparent',
  },
  code: {
    fontSize: responsiveFontSize(48),
    fontWeight: '700',
    letterSpacing: 2,
    marginTop: responsivePadding(8),
    zIndex: 1,
  },
  title: {
    fontSize: responsiveFontSize(20),
    fontWeight: '600',
    textAlign: 'center',
    marginTop: responsivePadding(8),
    zIndex: 1,
  },
  hint: {
    fontSize: responsiveFontSize(14),
    lineHeight: responsiveFontSize(20),
    textAlign: 'center',
    marginTop: responsivePadding(12),
    maxWidth: 400,
    zIndex: 1,
  },
  cta: {
    marginTop: responsivePadding(24),
    paddingHorizontal: 20,
    minHeight: 44,
    zIndex: 1,
  },
  ctaPressed: {
    opacity: 0.85,
  },
  ctaLabel: {
    color: '#fff',
    fontSize: responsiveFontSize(13),
    fontWeight: '500',
  },
});

export default NotFoundScreen;
